// antd components
import { Form, Input, Select } from "antd";

// components - atom
import Button from "components/atoms/Button/Button";
import Label from "components/atoms/Label/Label";
import Spinner from "components/atoms/Spinner/Spinner";

// components - molecules
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import { Head } from "components/molecules/Head/Head";
import HeaderStatusInfo from "components/molecules/HeaderStatusInfo/HeaderStatusInfo";
import PageTitle from "components/molecules/PageTitle/PageTitle";

// constants
import { PageTitles, StatusInfo, AccountManagement as Constants, ViewMode } from "constants/global";

// hook
import { useViewEditAccountPage } from "containers/AccountManagement/EditTenantAccount/useViewEditAccountPage";

// styles
import styles from "containers/AccountManagement/EditTenantAccount/viewEditAccount.module.css";
import theme from "styles/theme";

// utils
import getIcon from "utils/getIcon";

type ErrorMessageWrapperTypes = {
	fieldError?: boolean;
	message: string;
};

const { Option } = Select;

const ErrorMessageWrapper = ({ fieldError, message }: ErrorMessageWrapperTypes) => {
	return fieldError ? <ErrorMessage classes={styles?.err_message} message={message} /> : <></>;
};

const ViewEditAccountPage = () => {
	const componentProps = useViewEditAccountPage();
	const {
		mode,
		handleMode,
		goBack,
		onChange,
		onSearch,
		searchInput,
		fieldErrors,
		validateFields,
		adminData,
		handleChange,
		initialValues,
		loading,
		backgroundLoading,
		storesDataList,
		isUserActive,
		isDetailsUpdated,
		madeChangesHandler,
		handleDeactivateUser,
		activateUser,
		handleResendPassword,
		canResendPassword,
		islocked
	} = componentProps;
	if (loading) {
		return <Spinner />;
	}
	const usernameClasses = fieldErrors?.username ? styles?.err : styles?.filled;
	const emailClasses = fieldErrors?.email ? styles?.err : styles?.filled;

	return (
		<div className="min-h-screen relative">
			{backgroundLoading && <Spinner type="transparent" />}
			<div className={styles?.bg_curve}>{getIcon({ icon: "landingPageBackground" })}</div>
			<div className={styles?.bg_frame}>{getIcon({ icon: "businessDecisions" })}</div>

			<div className={styles?.root}>
				<Head>{PageTitles.EDIT_ACCOUNT}</Head>

				<HeaderStatusInfo message={StatusInfo.NO_ACTION_REQ} />
				<PageTitle showBackIcon={true} title={Constants.EDIT_ACCOUNT.EDIT_ACCOUNT_HEADER} subTitle={Constants.EDIT_ACCOUNT.EDIT_ACCOUNT_SUBHEADER} goBack={goBack} />
				<div className={styles?.main_wrapper}>
					<Form className={styles?.form} layout="vertical">
						<Label label={Constants.CREATE_ACCOUNT.TENANT_NAME_LABEL} classes={{ label: styles?.name_label }} />
						<div className="my-[1.25rem] items-center">
							<Form.Item name="outlet_code" className="p-0 md:w-[25rem]" initialValue={initialValues?.tenant_name}>
								<Select
									disabled={mode === ViewMode.READ}
									showSearch
									optionFilterProp="children"
									className={`tenant-search ${styles.inputSearch} ${searchInput ? styles?.filled : ""}`}
									placeholder={Constants.CREATE_ACCOUNT.SEARCH_LABEL}
									data-testid="search_input"
									listHeight={325}
									onChange={onChange}
									onSearch={onSearch}
									value={searchInput}
									dropdownStyle={theme.dropdownStyle}
									suffixIcon={<span className="cursor-pointer">{getIcon({ icon: "search" })}</span>}
									filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
								>
									{storesDataList.map((item) => {
										return (
											<Option value={`${item.tenant_name}*${item.outlet_code}*${item.tenant_code}`} key={item.tenant_code}>
												{`${item.tenant_name} ${item.tenant_code}`}
											</Option>
										);
									})}
								</Select>
							</Form.Item>
						</div>
						<div>
							<Form.Item
								name="username"
								className="mb-auto"
								initialValue={initialValues?.username}
								extra={<ErrorMessageWrapper fieldError={fieldErrors?.username} message={Constants.CREATE_ACCOUNT.INVALID_USERNAME} />}
								label={<Label label={Constants.CREATE_ACCOUNT.USERNAME_LABEL} classes={{ label: styles?.basic_input_label }} />}
							>
								<Input
									placeholder={Constants.CREATE_ACCOUNT.USERNAME_INPUT_PLACEHOLDER}
									className={`${styles.input_field} ${adminData?.username ? usernameClasses : ""}`}
									readOnly={mode === ViewMode.READ}
									disabled={mode === ViewMode.READ || mode === ViewMode.EDIT}
									onChange={handleChange}
									value={adminData?.username}
									onBlur={() => validateFields(false)}
									data-testid="username"
								/>
							</Form.Item>
							<Form.Item
								initialValue={initialValues?.email}
								className="mb-auto"
								extra={<ErrorMessageWrapper fieldError={fieldErrors?.email} message={Constants.CREATE_ACCOUNT.INVALID_EMAIL} />}
								name="email"
								label={<Label label={Constants.CREATE_ACCOUNT.EMAIL_LABEL} classes={{ label: styles?.basic_input_label }} />}
							>
								<Input
									placeholder={Constants.CREATE_ACCOUNT.EMAIL_INPUT_PLACEHOLDER}
									className={`${styles.input_field} ${adminData?.email ? emailClasses : ""}`}
									onChange={handleChange}
									readOnly={mode === ViewMode.READ}
									value={adminData.email}
									onBlur={() => validateFields(false)}
									data-testid="email"
								/>
							</Form.Item>
						</div>
						<div>
							<div className={styles?.button_wrapper}>
								{mode === ViewMode.READ && !islocked && (
									<>
										{isUserActive && <Button label={Constants.EDIT_ACCOUNT.DEACTIVATE_ACCOUNT} type="active" background="red" onClick={handleDeactivateUser} />}
										{isUserActive && canResendPassword && (
											<Button label={Constants.EDIT_ACCOUNT.RESEND_PASSWORD_BUTTON_LABEL} type="active" background="green" onClick={handleResendPassword} />
										)}
										{!isUserActive && <Button label={Constants.EDIT_ACCOUNT.ACTIVATE_ACCOUNT} type="active" background="green" onClick={activateUser} />}
									</>
								)}
								{mode === ViewMode.EDIT && isUserActive && (
									<>
										<Button label={Constants.EDIT_ACCOUNT.MAKE_CHANGES} type={isDetailsUpdated ? "active" : "disabled"} onClick={madeChangesHandler} />
									</>
								)}
							</div>
							{mode === ViewMode.READ && isUserActive && (
								<div className={styles?.bottom_text_link} onClick={() => handleMode(ViewMode.EDIT)}>
									<p className={styles?.textViolet}>{Constants.EDIT_ACCOUNT.EDIT_USER_LABEL}</p>
									<span className="ml-[0.5625rem] cursor-pointer ">{getIcon({ icon: "inWindow", iconColor: theme.colors.violet })}</span>
								</div>
							)}
							{mode === ViewMode.EDIT && isUserActive && (
								<div className={styles?.bottom_text_link} onClick={goBack}>
									<p className={styles?.textViolet}>{Constants.EDIT_ACCOUNT.GO_BACK}</p>
									<span className="ml-[0.5625rem] cursor-pointer">{getIcon({ icon: "inWindow", iconColor: theme.colors.violet })}</span>
								</div>
							)}
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};

export default ViewEditAccountPage;
